/**
 * Pure, framework-free statistics helpers for the "Labo Stats Keno" page.
 *
 * The 5 tools of the Labo are re-implementations (adapted to the 2025 FDJ
 * formula: 56 numbers, 16 drawn) of the methods observed in 5 legacy Excel
 * tools. Everything is computed client-side from the public draw history, so
 * these functions take an array of draws and return plain data : no React, no
 * network. They are unit-tested in `frontend/__tests__/laboStats.test.ts`.
 *
 * Draw-order convention: unless stated otherwise, `tiragesDesc` is ordered
 * most-recent-first (index 0 = latest draw), matching what `useTirages`
 * returns. "Écart" (gap) counts draws elapsed, so a current gap of 0 means the
 * item appeared in the latest draw.
 */

export const KENO_MAX_NUMBER = 56;
export const KENO_DRAW_SIZE = 16;

export interface LaboDraw {
  dateTirage: string;
  numeros: number[];
}

// ----------------------------------------------------------------------------
// Generic helpers
// ----------------------------------------------------------------------------

/** Median of a numeric list. Returns 0 for an empty list. */
export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Arithmetic mean of a numeric list. Returns 0 for an empty list. */
export function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

/**
 * Effective draw size (numbers drawn per draw), read from the data so the
 * helpers stay correct across formula changes. Falls back to KENO_DRAW_SIZE.
 */
export function effectiveDrawSize(tirages: LaboDraw[]): number {
  const lengths = tirages.map((t) => t.numeros.length).filter((n) => n > 0);
  const m = median(lengths);
  return m > 0 ? m : KENO_DRAW_SIZE;
}

// ----------------------------------------------------------------------------
// Tool 1 : Taux observé vs taux théorique
// ----------------------------------------------------------------------------

export type RateStatus = "retard" | "neutre" | "avance";

export interface NumberRateStats {
  number: number;
  appearances: number;
  observedRate: number; // 0-1
  theoreticalRate: number; // 0-1
  /** (observed - theoretical) as a signed share of the theoretical rate, in %. */
  deltaPct: number;
  status: RateStatus;
}

/**
 * Observed appearance rate per number vs the theoretical single-draw rate
 * (drawSize / maxNumber, e.g. 16/56 ≈ 28.6%). `bandPct` is the neutral band
 * around the theoretical rate (default ±10% relative): outside it a number is
 * flagged "en retard" (below) or "en avance" (above).
 */
export function computeNumberRateStats(
  tiragesDesc: LaboDraw[],
  maxNumber = KENO_MAX_NUMBER,
  bandPct = 10
): NumberRateStats[] {
  const total = tiragesDesc.length;
  const drawSize = effectiveDrawSize(tiragesDesc);
  const theoreticalRate = drawSize / maxNumber;

  const counts = new Array(maxNumber + 1).fill(0);
  for (const t of tiragesDesc) {
    for (const n of t.numeros) if (n >= 1 && n <= maxNumber) counts[n]++;
  }

  const out: NumberRateStats[] = [];
  for (let n = 1; n <= maxNumber; n++) {
    const appearances = counts[n];
    const observedRate = total > 0 ? appearances / total : 0;
    const deltaPct =
      theoreticalRate > 0 ? ((observedRate - theoreticalRate) / theoreticalRate) * 100 : 0;
    let status: RateStatus = "neutre";
    if (deltaPct < -bandPct) status = "retard";
    else if (deltaPct > bandPct) status = "avance";
    out.push({ number: n, appearances, observedRate, theoreticalRate, deltaPct, status });
  }
  return out;
}

// ----------------------------------------------------------------------------
// Tool : Top numéros sur une période (fréquence de sortie sur une plage de dates)
// ----------------------------------------------------------------------------

export interface RankedNumber {
  number: number;
  count: number;
}

/**
 * Keep the draws whose date (YYYY-MM-DD) falls within [startIso, endIso]
 * inclusive. Bounds are optional; an absent bound is unbounded on that side.
 * Comparison is lexicographic on the ISO date prefix (valid for YYYY-MM-DD).
 */
export function filterByDateRange(
  tiragesDesc: LaboDraw[],
  startIso?: string,
  endIso?: string
): LaboDraw[] {
  return tiragesDesc.filter((t) => {
    const d = String(t.dateTirage).slice(0, 10);
    if (startIso && d < startIso) return false;
    if (endIso && d > endIso) return false;
    return true;
  });
}

/**
 * Frequency ranking of every number over a set of draws: how many times each
 * number was drawn, sorted by count desc then number asc. Includes numbers
 * with a zero count (so the full 1..maxNumber range is present).
 */
export function frequencyRanking(draws: LaboDraw[], maxNumber = KENO_MAX_NUMBER): RankedNumber[] {
  const counts = new Array(maxNumber + 1).fill(0);
  for (const t of draws) {
    for (const n of t.numeros) if (n >= 1 && n <= maxNumber) counts[n]++;
  }
  const out: RankedNumber[] = [];
  for (let n = 1; n <= maxNumber; n++) out.push({ number: n, count: counts[n] });
  return out.sort((a, b) => b.count - a.count || a.number - b.number);
}

/** Retire N jours calendaires à une date ISO (YYYY-MM-DD). */
export function minusDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Vue par défaut de l'outil « Top numéros sur une période » : `days` jours
 * calendaires jusqu'au dernier tirage (bornes incluses), numéros jamais sortis
 * exclus. Même calcul que LaboStatsService::topPeriod() côté serveur.
 */
export function topPeriod(
  tiragesDesc: LaboDraw[],
  days = 30,
  topN = 10
): { start: string; end: string; drawCount: number; top: RankedNumber[] } {
  if (tiragesDesc.length === 0) return { start: "", end: "", drawCount: 0, top: [] };
  const end = String(tiragesDesc[0].dateTirage).slice(0, 10);
  const start = minusDays(end, days);
  const window = filterByDateRange(tiragesDesc, start, end);
  const top = frequencyRanking(window).filter((r) => r.count > 0).slice(0, topN);
  return { start, end, drawCount: window.length, top };
}

// ----------------------------------------------------------------------------
// Tool 2 : Consensus multi-fenêtres (signature durable)
// ----------------------------------------------------------------------------

export interface WindowSpec {
  label: string;
  size: number; // number of most-recent draws in the window
}

export interface ConsensusEntry {
  number: number;
  /** Frequency (0-1) of the number within each window, same order as windows. */
  freqByWindow: number[];
  /** 1-based rank of the number within each window (1 = most frequent). */
  rankByWindow: number[];
  /** True when the number is in the top-K of every window. */
  inAllWindows: boolean;
  /** Number of windows where the number is in the top-K. */
  windowsInTopK: number;
}

/**
 * For each number, its frequency and rank within several most-recent windows,
 * plus whether it stays in the top-K across all of them ("durable signature").
 * Returned sorted by windowsInTopK desc, then by average rank asc.
 */
export function computeConsensus(
  tiragesDesc: LaboDraw[],
  windows: WindowSpec[],
  topK = 15,
  maxNumber = KENO_MAX_NUMBER
): ConsensusEntry[] {
  // Per-window counts and ranks.
  const freqByWindow: number[][] = []; // [windowIdx][number]
  const rankByWindow: number[][] = [];
  for (const w of windows) {
    const slice = tiragesDesc.slice(0, Math.min(w.size, tiragesDesc.length));
    const counts = new Array(maxNumber + 1).fill(0);
    for (const t of slice) for (const n of t.numeros) if (n >= 1 && n <= maxNumber) counts[n]++;
    const freq = new Array(maxNumber + 1).fill(0);
    for (let n = 1; n <= maxNumber; n++) freq[n] = slice.length > 0 ? counts[n] / slice.length : 0;
    // Rank: 1 = highest count. Ties share the ascending order of appearance.
    const order = Array.from({ length: maxNumber }, (_, i) => i + 1).sort(
      (a, b) => counts[b] - counts[a] || a - b
    );
    const rank = new Array(maxNumber + 1).fill(0);
    order.forEach((n, i) => (rank[n] = i + 1));
    freqByWindow.push(freq);
    rankByWindow.push(rank);
  }

  const entries: ConsensusEntry[] = [];
  for (let n = 1; n <= maxNumber; n++) {
    const freqs = windows.map((_, wi) => freqByWindow[wi][n]);
    const ranks = windows.map((_, wi) => rankByWindow[wi][n]);
    const windowsInTopK = ranks.filter((r) => r >= 1 && r <= topK).length;
    entries.push({
      number: n,
      freqByWindow: freqs,
      rankByWindow: ranks,
      inAllWindows: windowsInTopK === windows.length,
      windowsInTopK,
    });
  }

  const avgRank = (e: ConsensusEntry) => mean(e.rankByWindow);
  return entries.sort((a, b) => b.windowsInTopK - a.windowsInTopK || avgRank(a) - avgRank(b));
}

/** Aperçu gratuit de l'outil « Numéros signature » : classement simple, zéros exclus. */
export function signatureTeaser(tiragesDesc: LaboDraw[], top = 15): RankedNumber[] {
  return frequencyRanking(tiragesDesc).filter((r) => r.count > 0).slice(0, top);
}

// ----------------------------------------------------------------------------
// Tool 3 : Générateur de grille
// ----------------------------------------------------------------------------

export type Rng = () => number;

/** Uniform random distinct grid of `size` numbers in [1, maxNumber]. */
export function generateRandomGrid(
  size: number,
  rng: Rng = Math.random,
  maxNumber = KENO_MAX_NUMBER
): number[] {
  const pool = Array.from({ length: maxNumber }, (_, i) => i + 1);
  // Partial Fisher-Yates.
  for (let i = 0; i < size && i < pool.length; i++) {
    const j = i + Math.floor(rng() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(size, pool.length)).sort((a, b) => a - b);
}

/**
 * Weighted random distinct grid: each number's chance of being picked is
 * proportional to its `weights[n]` (n is 1-based). Zero/absent weights fall
 * back to a small epsilon so every number remains reachable.
 */
export function generateWeightedGrid(
  size: number,
  weights: number[],
  rng: Rng = Math.random,
  maxNumber = KENO_MAX_NUMBER
): number[] {
  const remaining = Array.from({ length: maxNumber }, (_, i) => i + 1);
  const picked: number[] = [];
  const weightOf = (n: number) => Math.max(weights[n] ?? 0, 1e-6);

  while (picked.length < size && remaining.length > 0) {
    const totalWeight = remaining.reduce((s, n) => s + weightOf(n), 0);
    let r = rng() * totalWeight;
    let idx = 0;
    for (; idx < remaining.length; idx++) {
      r -= weightOf(remaining[idx]);
      if (r <= 0) break;
    }
    const chosen = remaining[Math.min(idx, remaining.length - 1)];
    picked.push(chosen);
    remaining.splice(remaining.indexOf(chosen), 1);
  }
  return picked.sort((a, b) => a - b);
}

export interface ScenarioVote {
  grid: number[];
  /** Citation count per number across all scenarios (1-based, length maxNumber+1). */
  citations: number[];
}

/**
 * Multi-scenario vote: draws `scenarioCount` grids of `scenarioSize` numbers,
 * counts how often each number is cited across scenarios, and returns the top
 * `size` most-cited numbers as the final grid.
 */
export function multiScenarioVote(
  size: number,
  scenarioCount: number,
  scenarioSize: number,
  gridFactory: () => number[],
  maxNumber = KENO_MAX_NUMBER
): ScenarioVote {
  const citations = new Array(maxNumber + 1).fill(0);
  for (let s = 0; s < scenarioCount; s++) {
    for (const n of gridFactory()) if (n >= 1 && n <= maxNumber) citations[n]++;
  }
  const grid = Array.from({ length: maxNumber }, (_, i) => i + 1)
    .sort((a, b) => citations[b] - citations[a] || a - b)
    .slice(0, size)
    .sort((a, b) => a - b);
  return { grid, citations };
}

/** Count matching numbers between a grid and a draw (for "check my grid" feedback). */
export function countMatches(grid: number[], draw: number[]): number {
  const set = new Set(draw);
  return grid.filter((n) => set.has(n)).length;
}
